import { useEffect } from "react";
import { useLocation } from "react-router-dom";

// Site-wide motion helper (no markup, renders nothing).
//  - fades/slides common blocks in as they enter the viewport
//  - draws a thin scroll-progress bar at the top
// Safe by design: elements are only hidden by JS after the observer exists, and
// a timeout reveals anything still hidden, so content can never stay invisible.
// Syntax kept ES2015-only (react-scripts 2.1.3: no ?. or ??).

var TARGETS = [
  ".card",
  ".ltn__category-item",
  ".features",
  ".ee-glass",
  ".mpd-card",
  ".eqc",
  ".ltn__product-item",
  ".ee-chips",
  ".alert",
  ".ltn__myaccount-tab-content-inner",
  ".eex-dash-hello",
  ".ag-courses_item",
].join(",");

var MotionLayer = function () {
  var location = useLocation();

  // scroll progress bar (once)
  useEffect(function () {
    if (typeof document === "undefined") return undefined;
    var bar = document.createElement("div");
    bar.className = "eex-progress";
    bar.setAttribute("aria-hidden", "true");
    document.body.appendChild(bar);
    var raf = 0;
    function update() {
      raf = 0;
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var p = h > 0 ? (window.scrollY / h) * 100 : 0;
      bar.style.width = Math.min(100, Math.max(0, p)) + "%";
    }
    function onScroll() {
      if (!raf) raf = window.requestAnimationFrame(update);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
    return function () {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
      if (bar.parentNode) bar.parentNode.removeChild(bar);
    };
  }, []);

  // reveal on scroll, re-armed on every route change and when new nodes mount
  useEffect(function () {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return undefined;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    var seen = typeof WeakSet !== "undefined" ? new WeakSet() : null;
    var io = new window.IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            en.target.classList.add("is-in");
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -6% 0px" }
    );

    var order = 0;
    function arm() {
      var nodes = document.querySelectorAll(TARGETS);
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (seen && seen.has(el)) continue;
        // never touch the header, drawers or modals
        if (el.closest("#topnav, .ltn__utilize, .modal, .notiflix-notify")) continue;
        if (seen) seen.add(el);
        el.classList.add("eex-reveal");
        el.style.setProperty("--eex-d", (order % 6) * 70 + "ms");
        order++;
        io.observe(el);
      }
    }

    arm();
    var mo = new window.MutationObserver(function () {
      window.requestAnimationFrame(arm);
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // failsafe: nothing may stay hidden
    var t = window.setTimeout(function () {
      var hidden = document.querySelectorAll(".eex-reveal:not(.is-in)");
      for (var j = 0; j < hidden.length; j++) {
        var r = hidden[j].getBoundingClientRect();
        if (r.top < window.innerHeight * 1.2) hidden[j].classList.add("is-in");
      }
    }, 1800);

    return function () {
      window.clearTimeout(t);
      mo.disconnect();
      io.disconnect();
    };
  }, [location.pathname]);

  return null;
};

export default MotionLayer;